import { $fetch, type $Fetch, type FetchContext, type FetchOptions } from 'ofetch'
import AuthModule from '~/repository/modules/auth'
import CareTeamsModule from '~/repository/modules/care-teams'
import CitiesModule from '~/repository/modules/cities'
import CmsModule from '~/repository/modules/cms'
import CommunicationModule from '~/repository/modules/communication'
import DeviceAssignmentsModule from '~/repository/modules/device-assignments'
import DevicesModule from '~/repository/modules/devices'
import DocumentsModule from '~/repository/modules/documents'
import EncountersModule from '~/repository/modules/encounters'
import EpisodeOfCareModule from '~/repository/modules/episodes-of-care'
import MonitorsModule from '~/repository/modules/monitor'
import ObservationsModule from '~/repository/modules/observations'
import OrganizationsModule from '~/repository/modules/organizations'
import PatientsModule from '~/repository/modules/patients'
import PractitionersModule from '~/repository/modules/practitioners'
import PriorityPatientsModule from '~/repository/modules/priority-patients'
import RelationshipsModule from '~/repository/modules/relationships'
import SearchModule from '~/repository/modules/search'
import SurveysModule from '~/repository/modules/surveys'
import TaskModule from '~/repository/modules/tasks'
import UsersModule from '~/repository/modules/users'
import VideoCallModule from '~/repository/modules/video-call'
import ContactPointsModule from '~/repository/modules/contact-points'
import PlacesModule from '~/repository/modules/places'
import PersonModule from '~/repository/modules/person'
import { v4 as uuidv4 } from 'uuid'
import MedocModule from '~/repository/modules/medoc'
import ClaimsModule from '~/repository/modules/claims'
import TimelineModule from '~/repository/modules/timeline'
import IncidentsModule from '~/repository/modules/incidents'
import AuthFactorsModule from '~/repository/modules/auth-factors'

export interface IApiInstance {
  auth: AuthModule
  cms: CmsModule
  monitors: MonitorsModule
  practitioners: PractitionersModule
  surveys: SurveysModule
  patients: PatientsModule
  episodesOfCare: EpisodeOfCareModule
  observations: ObservationsModule
  careTeams: CareTeamsModule
  communications: CommunicationModule
  deviceAssignments: DeviceAssignmentsModule
  users: UsersModule
  setToken: Function
  search: SearchModule
  relationship: RelationshipsModule
  organizations: OrganizationsModule
  devices: DevicesModule
  cities: CitiesModule
  videoCall: VideoCallModule
  encounters: EncountersModule
  priorityPatients: PriorityPatientsModule
  tasks: TaskModule
  documents: DocumentsModule
  contactPoints: ContactPointsModule
  places: PlacesModule
  claims: ClaimsModule
  persons: PersonModule
  medoc: MedocModule
  timeline: TimelineModule
  incidents: IncidentsModule
  authFactors: AuthFactorsModule
}

export enum TokenType {
  FIRESTORE = 'fire',
  DIRECTUS = 'directus',
  NONE = 'none',
}

export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()
  const userStore = useUserStore()
  const user = await getCurrentUser()
  let token: string
  if (user) {
    token = await user.getIdToken()
  }

  const fetcherFactory = (baseURL: string, tokenType: TokenType = TokenType.FIRESTORE): $Fetch => {
    const fetchOptions: FetchOptions = {}
    fetchOptions.baseURL = baseURL

    if (tokenType === TokenType.FIRESTORE) {
      fetchOptions.onRequest = onRequestInterceptor
    } else if (tokenType === TokenType.DIRECTUS) {
      fetchOptions.onRequest = onRequestInterceptorDirectus
    }
    fetchOptions.onResponseError = onResponseErrorInterceptor

    return $fetch.create(fetchOptions)
  }

  const onRequestInterceptor = async (context: FetchContext) => {
    if (user) {
      let token = await user.getIdToken()
      if (token) {
        context.options.headers = {
          ...context.options.headers,
          Authorization: `Bearer ${token}`,
          'x-request-id': uuidv4(),
        }
      }
    }
  }
  const onRequestInterceptorDirectus = (context: FetchContext) => {
    if (config.public.DIRECTUS_TOKEN) {
      context.options.headers = {
        ...context.options.headers,
        Authorization: `Bearer ${config.public.DIRECTUS_TOKEN}`,
      }
    }
  }

  const maxRetry = 3
  let retry = 1
  const onResponseErrorInterceptor = async (context: FetchContext) => {
    const notAuthorizeException = (context.options as any).notAuthorizeException == true
    if (notAuthorizeException) {
      throw createError({
        statusCode: context.response?.status || 500,
        statusMessage: context.response?.statusText || 'Unknown Error',
        data: context.response?._data || context.response,
      })
    }
    if (context.response?.status && context.response?.status === 401) {
      const user = await getCurrentUser()
      if (user && token && retry !== maxRetry && !notAuthorizeException) {
        const token = await user.getIdToken(true)
        userStore.saveFromToken(token)
        setToken(token)
        retry++
        return $fetch(context.request, context.options)
      } else if (!notAuthorizeException) {
        return navigateTo('/')
      }
    }
  }

  const setToken = (newToken: string) => {
    token = newToken
  }
  /** an object containing all repositories we need to expose */
  const modules: IApiInstance = {
    cms: new CmsModule(fetcherFactory(config.public.DIRECTUS_BASE_URL, TokenType.DIRECTUS)),
    cities: new CitiesModule(fetcherFactory(config.public.GEO_BASE_URL, TokenType.NONE)),
    monitors: new MonitorsModule(fetcherFactory(config.public.BACK_URL)),
    practitioners: new PractitionersModule(fetcherFactory(config.public.BACK_URL)),
    surveys: new SurveysModule(fetcherFactory(config.public.BACK_URL)),
    auth: new AuthModule(fetcherFactory(config.public.BACK_URL)),
    patients: new PatientsModule(fetcherFactory(config.public.BACK_URL)),
    episodesOfCare: new EpisodeOfCareModule(fetcherFactory(config.public.BACK_URL)),
    observations: new ObservationsModule(fetcherFactory(config.public.BACK_URL)),
    careTeams: new CareTeamsModule(fetcherFactory(config.public.BACK_URL)),
    communications: new CommunicationModule(fetcherFactory(config.public.BACK_URL)),
    users: new UsersModule(fetcherFactory(config.public.BACK_URL)),
    search: new SearchModule(fetcherFactory(config.public.BACK_URL)),
    relationship: new RelationshipsModule(fetcherFactory(config.public.BACK_URL)),
    organizations: new OrganizationsModule(fetcherFactory(config.public.BACK_URL)),
    videoCall: new VideoCallModule(fetcherFactory(config.public.BACK_URL)),
    devices: new DevicesModule(fetcherFactory(config.public.BACK_URL)),
    encounters: new EncountersModule(fetcherFactory(config.public.BACK_URL)),
    priorityPatients: new PriorityPatientsModule(fetcherFactory(config.public.BACK_URL)),
    tasks: new TaskModule(fetcherFactory(config.public.BACK_URL)),
    documents: new DocumentsModule(fetcherFactory(config.public.BACK_URL)),
    deviceAssignments: new DeviceAssignmentsModule(fetcherFactory(config.public.BACK_URL)),
    contactPoints: new ContactPointsModule(fetcherFactory(config.public.BACK_URL)),
    places: new PlacesModule(fetcherFactory(config.public.BACK_URL)),
    persons: new PersonModule(fetcherFactory(config.public.BACK_URL)),
    claims: new ClaimsModule(fetcherFactory(config.public.BACK_URL)),
    medoc: new MedocModule(fetcherFactory('http://localhost:3003', TokenType.NONE)),
    timeline: new TimelineModule(fetcherFactory(config.public.BACK_URL)),
    incidents: new IncidentsModule(fetcherFactory(config.public.BACK_URL)),
    authFactors: new AuthFactorsModule(fetcherFactory(config.public.BACK_URL)),
    setToken,
  }

  return {
    provide: {
      api: modules,
    },
  }
})
